import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, LinearProgress, Snackbar } from "@mui/material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  addDoc,
  collection,
  getDocs,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { getBlob, ref, uploadBytes } from "firebase/storage";
import { usePermissoesAcesso } from "../Utils/permissoesAcesso";
import "./relatoriodosagemPrint.css";
import RelatorioCalibracaoPrensaPrint from "../CalibracaoPrensa/RelatorioCalibracaoPrensaPrint";
import RelatorioGuiaDosagemPrint from "./RelatorioGuiaDosagemPrint";
import { auth, db, storage } from "../Config/firebase";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";
import LogoRelatorio from "../Components/LogoRelatorio/LogoRelatorio";
import { formatarMateriaisComConsumo } from "./CadastroDosagem";
import { NOME_CLIENTE_DOCUMENTO } from "../Config/branding";

const TIPO_LAUDO_DOSAGEM = "Relatório de Dosagens";

function formatarCodigoDosagem(valor = "", numeroRevisao = "0") {
  const codigo = String(valor).trim().toUpperCase().match(/\d+(?:\.[A-Z0-9]{1,5})?/)?.[0] || "";
  const revisaoTexto = String(numeroRevisao ?? "");
  const revisao =
    revisaoTexto.match(/REV\.?\s*(\d+)/i)?.[1] ||
    revisaoTexto.replace(/\D/g, "") ||
    "0";

  return codigo ? `T${codigo}-R${revisao}` : "-";
}

function dadosLaudoDosagem(dosagem = {}) {
  const nome = String(dosagem.traco || "").trim().toUpperCase();
  const correspondencia = nome.match(/^(T\d+(?:\.[A-Z0-9]{1,5})?)-REV\.?(\d+)(?:-|$)/i);

  if (!correspondencia) {
    throw new Error(
      "O traço deve começar no formato TXXX-REV.X, por exemplo T566-REV.0-TR4-GATE."
    );
  }

  return {
    descricao: nome,
    numeroTraco: correspondencia[1].toUpperCase(),
    numeroRevisao: correspondencia[2],
  };
}

function nomeSeguroArquivo(valor) {
  return String(valor || "RELATORIO-DOSAGEM")
    .replace(/[<>:"/\\|?*]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function dataAtualNomeArquivo() {
  return new Date().toLocaleDateString("pt-BR").replace(/\//g, "-");
}

function nomeArquivoRelatorioDosagem(dosagem = {}) {
  const nomeTraco = String(
    dosagem.traco ||
    dosagem.nomeTraco ||
    dosagem.codigo ||
    "RELATORIO-DOSAGEM"
  )
    .trim()
    .toUpperCase();

  return `${nomeSeguroArquivo(`${nomeTraco} - ${dataAtualNomeArquivo()}`)}.pdf`;
}

function aguardarRenderizacao() {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
  });
}

function blobParaDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(leitor.result);
    leitor.onerror = reject;
    leitor.readAsDataURL(blob);
  });
}

async function prepararImagensParaPdf(elementoRaiz) {
  const imagens = Array.from(elementoRaiz?.querySelectorAll("img") || []);
  const imagensAlteradas = [];

  await Promise.all(
    imagens.map(async (imagem) => {
      const srcOriginal = imagem.currentSrc || imagem.src;
      if (!srcOriginal || srcOriginal.startsWith("data:")) return;

      try {
        const storagePath = imagem.dataset.storagePath;
        let blob;

        if (storagePath) {
          blob = await getBlob(ref(storage, storagePath));
        } else {
          const resposta = await fetch(srcOriginal, { cache: "force-cache" });
          if (!resposta.ok) {
            throw new Error(`Falha ao carregar imagem (${resposta.status}).`);
          }
          blob = await resposta.blob();
        }

        const dataUrl = await blobParaDataUrl(blob);
        imagensAlteradas.push({ imagem, srcOriginal: imagem.src });
        imagem.src = dataUrl;

        if (typeof imagem.decode === "function") {
          await imagem.decode();
        }
      } catch (error) {
        console.warn("Não foi possível incorporar uma imagem ao PDF:", error);
      }
    })
  );

  return () => {
    imagensAlteradas.forEach(({ imagem, srcOriginal }) => {
      imagem.src = srcOriginal;
    });
  };
}

function numero(valor) {
  if (valor === "" || valor === null || valor === undefined) return null;

  if (typeof valor === "number") {
    return Number.isFinite(valor) ? valor : null;
  }

  const texto = String(valor).trim();

  if (texto.includes(",") && texto.includes(".")) {
    const n = Number(texto.replace(/\./g, "").replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }

  if (texto.includes(",")) {
    const n = Number(texto.replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }

  const n = Number(texto);
  return Number.isFinite(n) ? n : null;
}

function numeroBR(valor, casas = 1) {
  const n = typeof valor === "number" ? valor : numero(valor);
  if (n === null) return "-";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function formatarDataBR(data) {
  if (!data) return "-";
  if (typeof data === "string" && data.includes("-")) {
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  }
  if (data?.seconds) return new Date(data.seconds * 1000).toLocaleDateString("pt-BR");
  if (data?.toDate) return data.toDate().toLocaleDateString("pt-BR");
  return String(data);
}

function dataAtualBR() {
  return new Date().toLocaleDateString("pt-BR");
}

function adicionarDiasDataBR(dataBase, dias) {
  const quantidadeDias = numero(dias);
  if (!dataBase || quantidadeDias === null) return "-";
  const [ano, mes, dia] = String(dataBase).split("-").map(Number);
  const data = Number.isFinite(ano) ? new Date(ano, mes - 1, dia) : new Date(dataBase);
  if (Number.isNaN(data.getTime())) return "-";
  data.setDate(data.getDate() + quantidadeDias);
  return data.toLocaleDateString("pt-BR");
}

function adicionarDiasDataCurta(dataBase, dias) {
  const dataBR = adicionarDiasDataBR(dataBase, dias);
  if (dataBR === "-") return "-";
  const [dia, mes, ano] = dataBR.split("/");
  return dia && mes && ano ? `${dia}/${mes}/${String(ano).slice(-2)}` : dataBR;
}

function valorOuTraco(valor) {
  return valor === "" || valor === null || valor === undefined ? "-" : valor;
}

function textoAdicaoSuplementarAditivo(dosagem = {}) {
  const aditivos = Array.isArray(dosagem.aditivosResumo)
    ? dosagem.aditivosResumo
    : [];
  const pesoCimento = numero(dosagem.cimentoPeso) || 0;
  const nomeAditivo = (index) => {
    const aditivo = aditivos[index] || {};
    return String(
      aditivo.descricao || aditivo.Descricao || aditivo.material || `Aditivo ${index + 1}`
    ).trim();
  };
  const volumeAditivo = (percentual) => numeroBR(pesoCimento * (percentual / 100), 1) || "0,0";

  if (dosagem.caldaCimento === true) {
    return [
      "Caso a calda apresente fluidez inferior à classe de consistência especificada, admite-se adição suplementar de aditivo para corrigir a fluidez.",
      `A quantidade do aditivo ${nomeAditivo(0)} não pode ser reduzida sem que se observe retração significativa no topo do corpo de prova.`,
      `Caso as verificações de fluidez observadas no cone de Marsh fiquem abaixo do especificado, poderá ser reduzida a quantidade do aditivo ${nomeAditivo(1)}.`,
    ].join("\n");
  }

  return [
    "Caso o concreto apresente abatimento inferior à classe de consistência especificada, admite-se adição suplementar de aditivo para corrigir o abatimento.",
    "O volume máximo permitido de aditivo utilizado na mistura poderá ser de até 1,8% sobre o peso do cimento, conforme especificado pelo fabricante.",
    `1,8% equivale a ${volumeAditivo(1.8)} litros de aditivo ${nomeAditivo(0)} por m³ de concreto.`,
    `Recomenda-se que o volume máximo do aditivo ${nomeAditivo(1)} seja de até 0,5% sobre o peso de cimento, que equivale a ${volumeAditivo(0.5)} litros por metro cúbico de concreto.`,
  ].join("\n");
}

function textoAdicaoSuplementarAgua(dosagem = {}) {
  if (dosagem.caldaCimento === true) {
    return "Não se admite adição suplementar de água. Água suplementar é aquela adicionada acima da quantidade prevista no estudo de dosagem.";
  }

  return [
    "Não se admite adição suplementar de água. Água suplementar é aquela adicionada acima da quantidade prevista no estudo de dosagem.",
    "Para concretagens distantes recomendamos que o concreto saia da usina próximo do limite superior do \"slump\" evitando desta forma a adição de água na obra, mesmo sendo a folga de água do traço, porém caso seja adicionado girar o balão do caminhão betoneira em velocidade máxima o equivalente a 01 minuto por m³ do concreto.",
    "Fazer a coleta do concreto para moldagem de corpos de prova após o início do descarregamento do concreto, entre 15 a 85% do volume de concreto do caminhão.",
    "Concreto dosado para aplicação em até 5h30m.",
    "As proporções entre, cimento, agregado graúdo e agregado miúdo devem ser utilizadas conforme carta traço. A água retida do traço pode ou não ser utilizada durante a concretagem.",
    "Recomenda-se que no momento da aplicação a temperatura do concreto deve ser de no máximo 35 ºC, conforme item 5.8.1 da NBR 7212.",
  ].join("\n");
}

const OBSERVACOES_IMPORTANTES_PADRAO = [
  {
    titulo: "Adição suplementar de aditivo (NBR 7212 item 5.6.3)",
    texto: "Caso o concreto apresente abatimento inferior à classe de consistência especificada, admite-se adição suplementar de aditivo para corrigir o abatimento.",
  },
  {
    titulo: "Adição suplementar de água (NBR 7212 item 5.6.2)",
    texto: "Não se admite adição suplementar de água. Água suplementar é aquela adicionada acima da quantidade prevista no estudo de dosagem.\nAplicar o concreto com temperatura máxima de 35 °C.",
  },
];

function observacoesImportantes(dosagem = {}) {
  const lista = Array.isArray(dosagem.observacoesImportantes)
    ? dosagem.observacoesImportantes
    : [];

  const itensValidos = lista
    .map((item) => ({
      titulo: String(item?.titulo || "").trim(),
      texto: String(item?.texto || "").trim(),
      textoManual: item?.textoManual === true,
    }))
    .filter((item) => item.titulo || item.texto);

  const observacoes = itensValidos.length ? itensValidos : OBSERVACOES_IMPORTANTES_PADRAO;

  if (dosagem.usarTextosAutomaticosObservacoes !== true) return observacoes;

  return observacoes.map((item, index) => {
    if (index === 0 && !item.textoManual) return { ...item, texto: textoAdicaoSuplementarAditivo(dosagem) };
    if (index === 1 && !item.textoManual) return { ...item, texto: textoAdicaoSuplementarAgua(dosagem) };
    return item;
  });
}

function textoPreenchido(valor) {
  const texto = String(valor || "").trim();
  return texto !== "" && texto !== "-";
}

function materialVazio(item) {
  if (!item) return true;
  return ![
    item.descricao,
    item.Descricao,
    item.material,
    item.amostra,
    item.fornecedor,
    item.Fornecedor,
    item.massaEspecifica,
    item.MassaEspecifica,
  ].some(textoPreenchido);
}

function fckPrincipal(valor) {
  const texto = valorOuTraco(valor);
  if (texto === "-") return texto;
  return String(texto).split("/")[0].trim();
}

function primeiroValor(...valores) {
  return valores.find((valor) => valor !== "" && valor !== null && valor !== undefined);
}

function numeroBRUmaCasa(valor) {
  return numero(valor) !== null ? numeroBR(valor, 1) : "-";
}

function valorCampoOuPadrao(objeto, campo, padrao) {
  if (Object.prototype.hasOwnProperty.call(objeto || {}, campo)) {
    return objeto[campo] ?? "";
  }
  return padrao;
}

function formatarPrensaRuptura(prensa) {
  if (!prensa) return "";
  const partes = [
    prensa.codigo,
    prensa.empresa,
    prensa.equipamento,
    prensa.serie && `Serie ${prensa.serie}`,
    prensa.dataCalibracao && `Calibracao ${formatarDataBR(prensa.dataCalibracao)}`,
    prensa.dataVencimento && `Validade ${formatarDataBR(prensa.dataVencimento)}`,
  ].filter(Boolean);
  return partes.join(" - ");
}

function codigoMaterial(item) {
  return item.codigoFornecedor || item.fornecedorCodigo || item.codigoMaterial || item.codigo || "";
}

function materialLinha(item) {
  return {
    id: item.id,
        material: item.descricao || item.Descricao || item.Descrição || item.material || item.amostra || "",
    procedencia: item.procedencia || item.fornecedor || item.Fornecedor || "",
    localColeta: item.localColeta || "",
    tipoAgregado: item.tipoAgregado || "graudo",
    codigo: codigoMaterial(item),
    densidade:
  item.densidade?.densidadeMedia ||
  item.densidadeMedia ||
  (typeof item.densidade !== "object" ? item.densidade : "") ||
  item.massaEspecifica ||
  item.MassaEspecifica ||
  "",

massaUnitaria:
  item.massaUnitaria?.mediaMassaUnitaria ||
  item.mediaMassaUnitaria ||
  (typeof item.massaUnitaria !== "object" ? item.massaUnitaria : "") ||
  "",
  };
}

function ordenarAgregadosParaRelatorio(agregados = []) {
  return agregados
    .map((material, indiceOriginal) => ({ ...material, indiceOriginal }))
    .sort((a, b) => {
      const prioridadeA = a.tipoAgregado === "miudo" ? 0 : 1;
      const prioridadeB = b.tipoAgregado === "miudo" ? 0 : 1;
      return prioridadeA - prioridadeB || a.indiceOriginal - b.indiceOriginal;
    });
}

function massaEspecificaCimento(cimento, dosagem = {}) {
  return primeiroValor(
    dosagem.cimentoDensidade,
    cimento?.massaEspecifica,
    cimento?.MassaEspecifica,
    cimento?.densidade,
    cimento?.Densidade,
    "3,100"
  );
}

function instalarEstiloPrintDosagem() {
  const styleId = "print-style-dosagem";
  let style = document.getElementById(styleId);

  if (!style) {
    style = document.createElement("style");
    style.id = styleId;
    style.media = "print";
    document.head.appendChild(style);
  }

  style.textContent = `
    @media print {
      .relatorio-dosagem-print .acoes-print {
        display: none !important;
      }
    }
  `;
}

async function aguardarImagensRelatorio() {
  const imagens = Array.from(
    document.querySelectorAll(".relatorio-dosagem-print img")
  );

  await Promise.all(
    imagens.map(async (imagem) => {
      if (!imagem.src) return;
      if (imagem.complete) return;

      try {
        if (typeof imagem.decode === "function") {
          await imagem.decode();
          return;
        }
      } catch (_) {
        // Se o navegador falhar no decode, ainda aguardamos load/error abaixo.
      }

      if (imagem.complete) return;

      await new Promise((resolve) => {
        imagem.addEventListener("load", resolve, { once: true });
        imagem.addEventListener("error", resolve, { once: true });
      });
    })
  );
}

async function imprimir() {
  instalarEstiloPrintDosagem();
  await aguardarImagensRelatorio();
  window.print();
}

function calcularDados(dosagem, materiaisOriginais) {
  const materiais = (materiaisOriginais || []).map(materialLinha);
  const cimento = dosagem?.cimentoResumo || dosagem?.cimento || {};
  const agua = dosagem?.aguaResumo || dosagem?.agua || {
    descricao: dosagem?.aguaDescricao || "",
    fornecedor: dosagem?.aguaFornecedor || "",
    massaEspecifica: "1,000"
  };
  const aditivos = Array.isArray(dosagem?.aditivosResumo) ? dosagem.aditivosResumo : [];
  const adicoes = Array.isArray(dosagem?.adicoesResumo) ? dosagem.adicoesResumo : [];
  const adicoesVisiveis = adicoes.length ? adicoes : [];
  const agregados = materiais;
  const agregadosRelatorio = ordenarAgregadosParaRelatorio(agregados);

  const pesoCimento = numero(dosagem.cimentoPeso) || 0;
  const pesoAdicao = (index) =>
    materialVazio(adicoesVisiveis[index])
      ? 0
      : pesoCimento * ((numero(dosagem[`adicaoParcial${index}`]) || 0) / 100);
  const pesoAdicoes = adicoesVisiveis.reduce((total, _, index) => total + pesoAdicao(index), 0);
  const pesoLigante = pesoCimento + pesoAdicoes;
  const pesoAgua = pesoLigante * (numero(dosagem.fatorAc) || 0);
  const pesoAditivo = (index) => pesoLigante * ((numero(dosagem[`aditivoParcial${index}`]) || 0) / 100);
  const pesoAditivos = aditivos.reduce((total, _, index) => total + pesoAditivo(index), 0);
  const mEspCimento = massaEspecificaCimento(cimento, dosagem);
  const volumeCimento = pesoCimento / (numero(mEspCimento) || 1);
  const volumeAdicao = (index) =>
    materialVazio(adicoesVisiveis[index])
      ? 0
      : pesoAdicao(index) / (numero(adicoesVisiveis[index]?.massaEspecifica) || 1);
  const volumeAdicoes = adicoesVisiveis.reduce((total, _, index) => total + volumeAdicao(index), 0);
  const mEspAgua = numero(agua.massaEspecifica) || 1;
  const volumeAgua = pesoAgua / mEspAgua;
  const volumeAditivo = (index) => pesoAditivo(index) / (numero(aditivos[index]?.massaEspecifica) || 1);
  const volumeAditivos = aditivos.reduce((total, _, index) => total + volumeAditivo(index), 0);
  const teorArConsiderado = numero(dosagem.teorArConsiderado) ?? 0;
  const teorArEncontrado = numero(dosagem.teorArEncontrado);
  const teorArCorrigido = teorArEncontrado ?? teorArConsiderado;
  const volumeArCorrigido = teorArCorrigido * 10;
  const volumeBetoneira = numero(dosagem.volumeBetoneira);
  const ajusteBetoneiraPorMetroCubico = (valor) =>
    volumeBetoneira && volumeBetoneira > 0
      ? (numero(valor) || 0) / volumeBetoneira
      : 0;
  const pesoAdicaoCorrigido = (index) =>
    pesoAdicao(index) + ajusteBetoneiraPorMetroCubico(dosagem[`adicaoSobra${index}`]);
  const volumeAdicaoCorrigido = (index) =>
    materialVazio(adicoesVisiveis[index])
      ? 0
      : pesoAdicaoCorrigido(index) / (numero(adicoesVisiveis[index]?.massaEspecifica) || 1);
  const volumeAdicoesCorrigidas = adicoesVisiveis.reduce(
    (total, _, index) => total + volumeAdicaoCorrigido(index),
    0
  );
  const pesoLiganteCorrigido =
    pesoCimento +
    adicoesVisiveis.reduce((total, _, index) => total + pesoAdicaoCorrigido(index), 0);
  const pesoAguaCorrigido = pesoAgua + ajusteBetoneiraPorMetroCubico(dosagem.aguaSobra);
  const volumeAguaCorrigido = pesoAguaCorrigido / mEspAgua;
  const pesoAditivoCorrigido = (index) =>
    pesoAditivo(index) + ajusteBetoneiraPorMetroCubico(dosagem[`aditivoSobra${index}`]);
  const volumeAditivoCorrigido = (index) =>
    pesoAditivoCorrigido(index) / (numero(aditivos[index]?.massaEspecifica) || 1);
  const volumeAditivosCorrigidos = aditivos.reduce(
    (total, _, index) => total + volumeAditivoCorrigido(index),
    0
  );
  const percentualAdicaoCorrigido = (index) =>
    pesoCimento ? (pesoAdicaoCorrigido(index) / pesoCimento) * 100 : 0;
  const percentualAditivoCorrigido = (index) =>
    pesoLiganteCorrigido ? (pesoAditivoCorrigido(index) / pesoLiganteCorrigido) * 100 : 0;
  const fatorAcCorrigido = pesoLiganteCorrigido ? pesoAguaCorrigido / pesoLiganteCorrigido : null;
  const indiceMiudo = agregados.findIndex((material) => material.tipoAgregado === "miudo");
  const percentualMiudo = indiceMiudo >= 0 ? numero(dosagem[`materialParcial${indiceMiudo}`]) || 0 : 0;
const percentualGraudo =
  agregados.some((material) => material.tipoAgregado === "graudo")
    ? Math.max(0, 100 - percentualMiudo)
    : 0;

const percentualGrupoAgregado = (material) =>
  material.tipoAgregado === "graudo" ? percentualGraudo : percentualMiudo;
const somaProporcoesGrupo = (tipoAgregado) => agregados.reduce(
  (total, material, index) => material.tipoAgregado === tipoAgregado
    ? total + (numero(dosagem[`materialProporcaoGrupo${index}`]) || 0)
    : total,
  0
);
const percentualParcialAgregado = (material, index) => {
  const somaProporcoes = somaProporcoesGrupo(material.tipoAgregado);
  const proporcao = numero(dosagem[`materialProporcaoGrupo${index}`]) || 0;
  return somaProporcoes > 0
    ? percentualGrupoAgregado(material) * (proporcao / somaProporcoes)
    : 0;
};
  const percentualAgregadoTotal = (index) =>
    percentualParcialAgregado(agregados[index], index) / 100;
  const massaEspecificaMaterial = (material) =>
  numero(material.densidade) || 0;

const volumeArgamassaExperimental =
  volumeCimento + volumeAdicoesCorrigidas + volumeAguaCorrigido + volumeAditivosCorrigidos + volumeArCorrigido;

const volumeAgregadosExperimental =
  Math.max(0, 1000 - volumeArgamassaExperimental);

const somaParticipacaoAgregados = agregados.reduce(
  (total, _, index) => total + percentualAgregadoTotal(index),
  0
);

const divisorVolumeAgregados = agregados.reduce(
  (total, material, index) => {
    const participacao =
      somaParticipacaoAgregados > 0
        ? percentualAgregadoTotal(index) / somaParticipacaoAgregados
        : 0;

    return total + participacao / (numero(material.densidade) || 1);
  },
  0
);

const massaAgregadosExperimental =
  divisorVolumeAgregados > 0
    ? volumeAgregadosExperimental / divisorVolumeAgregados
    : 0;

  const pesoAgregado = (index) => {
  const participacao =
    somaParticipacaoAgregados > 0
      ? percentualAgregadoTotal(index) / somaParticipacaoAgregados
      : 0;

  return massaAgregadosExperimental * participacao;
};
  const somaPesos =
    pesoCimento +
    adicoesVisiveis.reduce((total, _, index) => total + pesoAdicaoCorrigido(index), 0) +
    pesoAguaCorrigido +
    aditivos.reduce((total, _, index) => total + pesoAditivoCorrigido(index), 0) +
    agregados.reduce((total, _, index) => total + pesoAgregado(index), 0);
  const pesoAgregadosMiudos = agregados.reduce(
    (total, material, index) => total + (material.tipoAgregado === "miudo" ? pesoAgregado(index) : 0),
    0
  );
  const pesoAgregadosGraudos = agregados.reduce(
    (total, material, index) => total + (material.tipoAgregado === "graudo" ? pesoAgregado(index) : 0),
    0
  );
  const teorArgamassa =
    pesoLiganteCorrigido + pesoAgregadosMiudos + pesoAgregadosGraudos
      ? ((pesoLiganteCorrigido + pesoAgregadosMiudos) / (pesoLiganteCorrigido + pesoAgregadosMiudos + pesoAgregadosGraudos)) * 100
      : null;

  const pesoSacoCimento = numero(dosagem.pesoSacoCimento) ?? 50;
  const fatorSacoCimento = pesoCimento > 0 ? pesoSacoCimento / pesoCimento : 0;
  const pesoAgregadoPorSaco = (index) => pesoAgregado(index) * fatorSacoCimento;
  const volumeAgregadoPorSaco = (material, index) => {
    const massaUnitaria = numero(material.massaUnitaria);
    return massaUnitaria && fatorSacoCimento > 0
      ? pesoAgregadoPorSaco(index) / massaUnitaria
      : null;
  };
  const medidaPadiolaA = (index) => numero(dosagem[`medidaPadiolaA${index}`]) ?? 35;
  const medidaPadiolaB = (index) => numero(dosagem[`medidaPadiolaB${index}`]) ?? 45;
  const quantidadePadiolas = (index) => numero(dosagem[`quantidadePadiolas${index}`]) ?? 2;
  const alturaPadiola = (material, index) => {
    const quantidade = quantidadePadiolas(index);
    const areaPadiola = (medidaPadiolaA(index) * medidaPadiolaB(index)) / 1000;
    const volume = volumeAgregadoPorSaco(material, index);
    return quantidade && areaPadiola && volume !== null
      ? volume / quantidade / areaPadiola
      : null;
  };

  const idades = [0, 1, 2, 3, 4, 5].map((index) => {
    const padrao = ["3", "7", "", "28", "", ""][index];
    const valor = valorCampoOuPadrao(dosagem, `idadeRuptura${index}`, padrao);
    return { index, valor, label: valor ? `${valor} dias` : "-" };
  });

  const valorRupturaTon = (cp, idade) =>
  dosagem[`rupturaCp${cp}TonIdade${idade.index}`] || "";

  const valorCp = (cp, idade) => {
  if (!valorRupturaTon(cp, idade)) return "";

  return primeiroValor(
    dosagem[`rupturaCp${cp}Idade${idade.index}`],
    dosagem[`rupturaCp${cp}${idade.label}`],
    dosagem[`rupturaCp${cp}${idade.index}`],
    dosagem[`cp${cp}Idade${idade.index}`],
    dosagem[`cp${cp}${idade.index}`],
    dosagem[`ruptura${cp}Idade${idade.index}`],
    ""
  );
};
  const valoresCp = (idade) => {
    const cp1 = numero(valorCp(1, idade));
    const cp2 = numero(valorCp(2, idade));
    return { cp1, cp2 };
  };
  const maximo = (idade) => {
    if (!idade.valor) return null;
    const { cp1, cp2 } = valoresCp(idade);
    const max = Math.max(cp1 ?? 0, cp2 ?? 0);
    return max || null;
  };
  const rendimento = (idade) => {
    const valor = maximo(idade);
    return pesoLigante ? valor / pesoLigante : null;
  };
  const crescimento = (idade) => {
    const atual = rendimento(idade);
    const indice = idades.findIndex((item) => item.index === idade.index);
    const anteriorIdade = [...idades].slice(0, indice).reverse().find((item) => item.valor);
    const anterior = anteriorIdade ? rendimento(anteriorIdade) : null;
    return atual !== null && atual && anterior !== null ? ((atual - anterior) / atual) * 100 : null;
  };

  const fckEst = (() => {
    const fck = numero(fckPrincipal(dosagem.fck));
    const sd = numero(dosagem.sd);
    return fck !== null && sd !== null ? fck + 1.65 * sd : null;
  })();
  const massaEspecificaFresca =
  numero(dosagem.massaEspecificaObtida) ??
  numero(dosagem.massaEspecificaFresca) ??
  numero(dosagem.massaEspecificaConcreto) ??
  null;
  const slumpObtido = primeiroValor(dosagem.abatimentoSlump, dosagem.slumpObtido, dosagem.slumpFinal, "");

  return {
  cimento,
  agua,
  aditivos,
  adicoes: adicoesVisiveis,
  agregados,
  agregadosRelatorio,
  pesoAdicao: pesoAdicaoCorrigido,
  volumeAdicao: volumeAdicaoCorrigido,
  percentualAdicaoCorrigido,
    pesoCimento,
    mEspCimento,
    pesoAgua: pesoAguaCorrigido,
    mEspAgua,
    pesoAditivo: pesoAditivoCorrigido,
    percentualAditivoCorrigido,
    volumeCimento,
    volumeAgua: volumeAguaCorrigido,
    volumeAditivo: volumeAditivoCorrigido,
    pesoAgregado,
   
percentualParcialAgregado,
massaEspecificaAgregado: (material) =>
  numero(material.densidade) || null,

volumeAgregado: (material, index) => {
  const massaEsp = numero(material.densidade);

  if (!massaEsp) return null;

  return pesoAgregado(index) / massaEsp;
},
    somaPesos,
    teorArConsiderado: teorArCorrigido,
    volumeArConsiderado: volumeArCorrigido,
    fatorAcCorrigido,
    teorArgamassa,
    pesoSacoCimento,
    fatorSacoCimento,
    pesoAgregadoPorSaco,
    volumeAgregadoPorSaco,
    medidaPadiolaA,
    medidaPadiolaB,
    quantidadePadiolas,
    alturaPadiola,
    idades,
    valorCp,
    valoresCp,
    maximo,
    crescimento,
    fckEst,
    massaEspecificaFresca,
    slumpObtido,
  };
}

function GraficoResistencia({ dados }) {
  const pontos = dados.idades.map((idade, index) => ({
    index,
    valor: idade.valor ? dados.maximo(idade) : null,
  }));
  const valores = pontos.map((ponto) => ponto.valor).filter((valor) => valor !== null);
  const max = Math.max(1, ...valores);
  const pontosValidos = pontos.filter((ponto) => ponto.valor !== null && ponto.valor > 0);
  const larguraColuna = 100 / dados.idades.length;
  const pontosLinha = pontosValidos.map((ponto) => ({
    x: larguraColuna * ponto.index + larguraColuna / 2,
    y: 100 - Math.max(8, (ponto.valor / max) * 82),
  }));
  const polyline = pontosLinha.map((ponto) => `${ponto.x},${ponto.y}`).join(" ");

  return (
    <div className="grafico-resistencia">
      <div className="grafico-barras">
        {pontos.map((ponto) => (
          <div className="barra-coluna" key={`barra-${ponto.index}`}>
            {ponto.valor ? <div className="barra" style={{ height: `${Math.max(18, (ponto.valor / max) * 82)}%` }} /> : null}
          </div>
        ))}
      </div>
      <svg className="linha-resistencia" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {pontosLinha.length > 1 && <polyline points={polyline} />}
        {pontosLinha.map((ponto, index) => (
          <polygon
            key={`ponto-${index}`}
            points={`${ponto.x - 0.45},${ponto.y + 2.2} ${ponto.x + 0.45},${ponto.y + 2.2} ${ponto.x},${ponto.y - 2.2}`}
          />
        ))}
      </svg>
    </div>
  );
}

function CelulaFck({ dados, idade }) {
  if (!idade.valor) {
    return (
      <div className="fck-valores">
        <span>-</span><span>-</span><span>-</span>
      </div>
    );
  }

  const { cp1, cp2 } = dados.valoresCp(idade);
  const max = dados.maximo(idade);
  const valorCp1 = numeroBRUmaCasa(cp1);
  const valorCp2 = numeroBRUmaCasa(cp2);
  const cp1Max = cp1 !== null && max !== null && cp1 === max;
  const cp2Max = cp2 !== null && max !== null && cp2 === max;

  return (
    <div className="fck-valores">
      <span className={cp1Max ? "valor-maximo" : ""}>{valorCp1}</span>
      <span>-</span>
      <span className={cp2Max ? "valor-maximo" : ""}>{valorCp2}</span>
    </div>
  );
}

function RodapeResistencia({ dosagem, dados }) {
  return (
    <div className="rodape-resistencia">
      <div className="rodape-item">
        <span className="rodape-label">Desvio padrão considerado</span>
        <span className="rodape-valor">{numeroBR(dosagem.sd, 1)} MPa</span>
      </div>

      <div className="rodape-item">
        <span className="rodape-label">fckest</span>
        <span className="rodape-valor">{numeroBR(dados.fckEst, 1)} MPa</span>
      </div>

      <div className="rodape-item">
        <span className="rodape-label">Massa específica fresca</span>
        <span className="rodape-valor">
          {numeroBR(dados.massaEspecificaFresca, 3)} g/cm³
        </span>
      </div>

      <div className="rodape-item">
        <span className="rodape-label">CPs moldados</span>
        <span className="rodape-valor">{valorOuTraco(dosagem.cps)}</span>
      </div>

      <div className="rodape-item">
        <span className="rodape-label">Slump obtido</span>
        <span className="rodape-valor">
          {valorOuTraco(dados.slumpObtido)} mm
        </span>
      </div>
    </div>
  );
}

function SecaoTitulo({ numero, children }) {
  return <div className="titulo-secao">{numero} {children}</div>;
}

function Subtitulo({ numero, children }) {
  return <div className="subtitulo-faixa">{numero} {children}</div>;
}

function AssinaturaResponsavelTecnico() {
  return (
    <div className="assinatura-tecnica">
      <img src="/Images/assinaturaLeal.png" alt="Assinatura" />
      <div className="linha-assinatura" />
      <strong>José Carlos Lemes Leal</strong>
      <span>Eng. Civil Esp. CREA 203379/D-TO</span>
    </div>
  );
}

function RodapeRestrito() {
  return (
    <div className="rodape-restrito">
      <div className="linha-rodape" />
      <div>DOCUMENTO PARA USO RESTRITO. REPRODUÇÃO/CÓPIA PROIBIDA SEM AUTORIZAÇÃO DA EMPRESA</div>
    </div>
  );
}

function dividirEmPaginas(lista, quantidade) {
  const paginas = [];
  for (let i = 0; i < lista.length; i += quantidade) {
    paginas.push(lista.slice(i, i + quantidade));
  }
  return paginas;
}

function CabecalhoRelatorio({ dosagem, modeloCalda = false }) {
  return (
    <>
      <table className="cabecalho cabecalho-modelo">
        <tbody>
          <tr>
            <td rowSpan="3" className="logo-cell"><LogoRelatorio alt="Logo" className="logo"  /></td>
            <td className="titulo-sgq">
              {modeloCalda ? "SISTEMA DE GESTÃO DA QUALIDADE" : "SISTEMA DE GESTAO DA QUALIDADE"}
            </td>
            <td className="codigo-cell">RQ 11.1</td>
          </tr>
          <tr>
            <td rowSpan="2" className="titulo-documento">
              {modeloCalda ? "Concreto - Laudo de dosagem - Carta traço" : "Concreto - Laudo de dosagem - Carta traco"}
            </td>
            <td>{modeloCalda ? "Revisão 00" : "Revisao 00"}</td>
          </tr>
          <tr>
            <td>20/08/2025</td>
          </tr>
        </tbody>
      </table>

      <table className="linha-data-codigo">
        <tbody>
          <tr>
            <td><strong>DATA</strong> <span>{dataAtualBR()}</span></td>
            <td>
              <strong>{modeloCalda ? "CÓDIGO:" : "CODIGO:"}</strong>{" "}
              {formatarCodigoDosagem(
                dosagem.codigo,
                dosagem.numeroRevisao ?? dosagem.traco
              )}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="faixa-cinza"></div>

      <div className="bloco-empresa">
        <div><strong>INTERESSADO:</strong> <span>{dosagem.clienteNome || "-"}</span></div>
        <div><strong>OBRA:</strong> <span>{dosagem.obraNome || "-"}</span></div>
      </div>
      <div className="linha-execucao-traco">
        <span>{modeloCalda ? "Data da execução do traço:" : "Data da execucao do traco:"}</span>
        <strong>{formatarDataBR(dosagem.data)}</strong>
      </div>
    </>
  );
}


function RelatorioFotografico({ dosagem, fotos = [] }) {
  if (!fotos.length) return null;

  return dividirEmPaginas(fotos, 6).map((paginaFotos, paginaIndex) => (
    <div className="pagina pagina-fotografica" key={`pagina-fotos-${paginaIndex}`}>
      <CabecalhoRelatorio dosagem={dosagem} />
      <div className="faixa-relatorio-fotografico">RELATÓRIO FOTOGRÁFICO</div>

      <div className="grade-fotografica">
        {paginaFotos.map((foto, index) => (
          <div className="foto-relatorio-item" key={foto.id || foto.url || `${paginaIndex}-${index}`}>
            <div className="foto-relatorio-imagem">
              <img
                src={foto.url}
                data-storage-path={foto.path || undefined}
                alt={foto.legenda || `Foto ${paginaIndex * 6 + index + 1}`}
              />
            </div>
            <div className="foto-relatorio-legenda">
              {foto.legenda || `FOTO ${String(paginaIndex * 6 + index + 1).padStart(2, "0")}`}
            </div>
          </div>
        ))}
      </div>

      <RodapeRestrito />
    </div>
  ));
}


function NormasMetodologia({ caldaCimento, metodologiasRelatorio }) {
  const normasSalvas = Array.isArray(metodologiasRelatorio)
    ? metodologiasRelatorio
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    : [];

  const normas = normasSalvas.length
    ? normasSalvas
    : caldaCimento
      ? [
          "NBR 16887/20 - Concreto - Determinação do teor de ar em concreto fresco - Método pressométrico;",
          "NBR 16889/20 - Concreto - Determinação da consistência pelo abatimento do tronco de cone;",
          "NBR 5738/15 - Concreto - Moldagem e Cura de Corpos de Prova Cilíndricos ou Prismáticos de Concreto;",
          "NBR 5739/18 - Concreto - Ensaio de Compressão de Corpos de Prova Cilíndricos;",
          "NBR 12821/09 - Preparação de Concreto em Laboratório - Procedimento.",
          "NBR 7681-1/13 - Calda de cimento para injeção - Requisitos.",
        ]
      : [
          "NBR 5738/15 - Concreto - Moldagem e Cura de Corpos de Prova Cilíndricos ou Prismáticos de Concreto;",
          "NBR 5739/18 - Concreto - Ensaio de Compressão de Corpos de Prova Cilíndricos;",
          "NBR 12821/09 - Preparação de Concreto em Laboratório - Procedimento.",
        ];

  return (
    <ul className="lista-normas">
      {normas.map((norma, index) => (
        <li key={`norma-${index}`}>{norma}</li>
      ))}
    </ul>
  );
}

function MaterialComParametro({ nome, parametro }) {
  return (
    <div className="material-parametro">
      <span>{nome}</span>
      <span>{parametro}</span>
    </div>
  );
}

function PaginaRelatorioCaldaCimento({ dosagem, dados }) {
  const textoIdades = dados.idades
    .filter((idade) => idade.valor)
    .map((idade) => String(idade.valor).padStart(2, "0"))
    .join(", ")
    .replace(/,([^,]*)$/, " e$1");
  const fluidez = primeiroValor(dosagem.fluidez, dosagem.slump, "");
  const toleranciaFluidez = primeiroValor(
    dosagem.toleranciaFluidez,
    dosagem.toleranciaSlumpProjeto,
    ""
  );

  return (
    <div className="pagina pagina-laudo-dosagem">
      <CabecalhoRelatorio dosagem={dosagem} modeloCalda />

      <div className="faixa-relatorio-ensaio">RELATÓRIO DE ENSAIO</div>

      <section className="secao-dosagem">
        <SecaoTitulo numero="1.0">OBJETIVO</SecaoTitulo>
        <p className="texto">
          Este relatório tem por finalidade apresentar os resultados obtidos referentes ao estudo de
          calda de cimento com materiais coletados e enviados pelo interessado, executado um traço
          experimental com base nos parâmetros fixados para a dosagem requerida.
        </p>
        <p className="texto">
          A dosagem foi executada em betoneira estacionária com capacidade real de 50 litros. Após a
          mistura dos materiais, realizou-se o ensaio para determinação das propriedades da calda no
          estado fresco, incluindo fluidez, massa específica e teor de ar aprisionado.
        </p>
        <p className="texto">
          Com a obtenção da fluidez requerida, moldaram-se corpos de prova conforme a NBR 5738 para
          determinação das resistências à compressão nas idades de {textoIdades || "-"} dias, em
          consonância com a NBR 5739.
        </p>
      </section>

      <section className="secao-dosagem">
        <SecaoTitulo numero="2.0">METODOLOGIA</SecaoTitulo>
        <p className="texto">Os ensaios seguiram as prescrições das normas da ABNT:</p>
        <NormasMetodologia
          caldaCimento
          metodologiasRelatorio={dosagem.metodologiasRelatorio}
        />
      </section>

      <section className="secao-dosagem">
        <SecaoTitulo numero="3.0">CARACTERÍSTICA DA CALDA DE CIMENTO</SecaoTitulo>
        <table className="caracteristica tabela-compacta">
          <colgroup>
            <col className="col-caracteristica-label" />
            <col className="col-caracteristica-traco" />
            <col className="col-caracteristica-label" />
            <col className="col-caracteristica-tipo" />
            <col className="col-caracteristica-label" />
            <col className="col-caracteristica-fck" />
            <col className="col-caracteristica-label" />
            <col className="col-caracteristica-slump" />
            <col className="col-caracteristica-label-teor" />
            <col className="col-caracteristica-teor" />
          </colgroup>
          <tbody>
            <tr>
              <td className="celula-label">Traço</td>
              <td className="celula-valor texto-nowrap">{dosagem.traco || "-"}</td>
              <td className="celula-label">Tipo</td>
              <td className="celula-valor texto-nowrap">
                {["Calda de cimento", dosagem.tipoLancamento || dosagem.tipoConcreto]
                  .filter(Boolean)
                  .join(" - ")}
              </td>
              <td className="celula-label">fck</td>
              <td className="celula-valor texto-nowrap">{fckPrincipal(dosagem.fck)} MPa</td>
              <td className="celula-label">Fluidez</td>
              <td className="celula-valor texto-nowrap">
                {fluidez
                  ? `${fluidez}${toleranciaFluidez ? ` ± ${toleranciaFluidez}` : ""} s`
                  : "-"}
              </td>
              <td className="celula-label">Teor ar</td>
              <td className="celula-valor texto-nowrap">{numeroBR(dados.teorArConsiderado, 1)}%</td>
            </tr>
          </tbody>
        </table>

        <Subtitulo numero="3.1">Materiais e consumo</Subtitulo>
        <table className="tabela-compacta tabela-materiais">
          <colgroup>
            <col className="col-materiais-material" />
            <col className="col-materiais-fornecedor" />
            <col className="col-materiais-fabricante" />
            <col className="col-materiais-consumo" />
            <col className="col-materiais-massa" />
            <col className="col-materiais-volume" />
          </colgroup>
          <thead>
            <tr>
              <th>Materiais</th>
              <th>Fornecedor/Tipo</th>
              <th>Fabricante e/ou origem</th>
              <th>Consumo<br />(kg/m³)</th>
              <th>M. Esp.<br />(g/cm³)</th>
              <th>Volume<br />(L/m³)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>CIMENTO</th>
              <td>{dados.cimento.descricao || "-"}</td>
              <td>{dados.cimento.fornecedor || "-"}</td>
              <td>{numeroBR(dados.pesoCimento, 0)}</td>
              <td>{valorOuTraco(dados.mEspCimento)}</td>
              <td>{numeroBR(dados.volumeCimento, 1)}</td>
            </tr>
            <tr>
              <th>
                <MaterialComParametro
                  nome="ÁGUA"
                  parametro={`a/c: ${numeroBR(dados.fatorAcCorrigido, 2)}`}
                />
              </th>
              <td>{dados.agua.descricao || "-"}</td>
              <td>{dados.agua.fornecedor || dados.agua.localColeta || "-"}</td>
              <td>{numeroBR(dados.pesoAgua, 0)}</td>
              <td>{numeroBR(dados.mEspAgua, 3)}</td>
              <td>{numeroBR(dados.volumeAgua, 1)}</td>
            </tr>
            {dados.aditivos.map((aditivo, index) => (
              <tr key={aditivo.id || `calda-aditivo-${index}`}>
                <th>
                  <MaterialComParametro
                    nome={`ADITIVO ${index + 1}`}
                    parametro={`${numeroBR(dados.percentualAditivoCorrigido(index), 2)}%`}
                  />
                </th>
                <td>{aditivo.descricao || "-"}</td>
                <td>{aditivo.fornecedor || "-"}</td>
                <td>{numeroBR(dados.pesoAditivo(index), 1)}</td>
                <td>{valorOuTraco(aditivo.massaEspecifica)}</td>
                <td>{numeroBR(dados.volumeAditivo(index), 1)}</td>
              </tr>
            ))}
            <tr>
              <th>
                <MaterialComParametro
                  nome="TEOR DE AR"
                  parametro={`${numeroBR(dados.teorArConsiderado, 1)}%`}
                />
              </th>
              <td>-</td><td>-</td><td>-</td><td>-</td>
              <td>{numeroBR(dados.volumeArConsiderado, 1)}</td>
            </tr>
            <tr className="calda-total">
              <th colSpan="3">Soma</th>
              <td>{numeroBR(dados.somaPesos, 0)}</td>
              <td>-</td>
              <td>1.000</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="secao-dosagem">
        <Subtitulo numero="3.2">Dados das rupturas</Subtitulo>
        <table className="resistencia tabela-compacta">
          <thead>
            <tr>
              <th colSpan={dados.idades.length + 1}>RESISTÊNCIA À COMPRESSÃO OBTIDOS</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>IDADE (DIAS)</td>
              {dados.idades.map((idade) => <td key={`calda-idade-${idade.index}`}>{idade.valor || "-"}</td>)}
            </tr>
            <tr>
              <td>DATA</td>
              {dados.idades.map((idade) => (
                <td key={`calda-data-${idade.index}`}>
                  {idade.valor ? adicionarDiasDataCurta(dosagem.data, idade.valor) : "-"}
                </td>
              ))}
            </tr>
            <tr>
              <td>fck (MPa)</td>
              {dados.idades.map((idade) => (
                <td key={`calda-fck-${idade.index}`}><CelulaFck dados={dados} idade={idade} /></td>
              ))}
            </tr>
            <tr>
              <td>Gráfico</td>
              <td colSpan={dados.idades.length}><GraficoResistencia dados={dados} /></td>
            </tr>
            <tr>
              <td>Crescimento (%)</td>
              {dados.idades.map((idade) => (
                <td key={`calda-crescimento-${idade.index}`}>
                  {idade.index === 0 || !idade.valor ? "-" : `${numeroBR(dados.crescimento(idade), 1)}%`}
                </td>
              ))}
            </tr>
            <tr>
              <td colSpan={dados.idades.length + 1} className="resistencia-rodape-cell">
                <RodapeResistencia dosagem={dosagem} dados={dados} />
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="secao-dosagem">
        <Subtitulo numero="3.3">
          Traço para 01 saco de cimento de {numeroBR(dados.pesoSacoCimento, 0)} kg
        </Subtitulo>
        <table className="tabela-compacta">
          <tbody>
            <tr>
              <th>CIMENTO:</th>
              <td>{numeroBR(dados.pesoSacoCimento, 0)} kg</td>
              <th>ÁGUA:</th>
              <td>{numeroBR(dados.pesoAgua * dados.fatorSacoCimento, 1)} litros</td>
              {dados.aditivos.map((aditivo, index) => (
                <React.Fragment key={aditivo.id || `calda-saco-aditivo-${index}`}>
                  <th>{aditivo.descricao || `Aditivo ${index + 1}`}:</th>
                  <td>{numeroBR(dados.pesoAditivo(index) * dados.fatorSacoCimento, 2)} kg</td>
                </React.Fragment>
              ))}
            </tr>
          </tbody>
        </table>
      </section>

      <section className="observacoes-importantes">
        <SecaoTitulo numero="4.0">OBSERVAÇÕES IMPORTANTES</SecaoTitulo>
        {observacoesImportantes(dosagem).map((item, index) => (
          <React.Fragment key={`${item.titulo}-${index}`}>
            <Subtitulo numero={`4.${index + 1}`}>{item.titulo || "Observação"}</Subtitulo>
            {String(item.texto || "")
              .split(/\r?\n/)
              .filter((linha) => linha.trim())
              .map((linha, linhaIndex) => (
                <p className="texto" key={`calda-observacao-${index}-${linhaIndex}`}>{linha}</p>
              ))}
          </React.Fragment>
        ))}
        {dados.slumpObtido && (
          <p className="texto calda-fluidez-obtida">
            Fluidez obtida no ensaio: {dados.slumpObtido} segundos.
          </p>
        )}
      </section>

      <AssinaturaResponsavelTecnico />
      <RodapeRestrito />
    </div>
  );
}

export default function RelatorioDosagemPrint({
  dosagem,
  materiais = [],
  fotosRelatorio = [],
  mostrarDosagemVolume = false,
  onVoltar,
}) {
  const { podeEditarDosagem } = usePermissoesAcesso();
  const somenteLeitura = !podeEditarDosagem;
  const relatorioRef = useRef(null);
  const arrasteRelatorioRef = useRef(null);
  const [salvandoLaudo, setSalvandoLaudo] = useState(false);
  const [laudoEnviado, setLaudoEnviado] = useState(false);
  const [imprimindo, setImprimindo] = useState(false);
  const [zoomAcimaDe150, setZoomAcimaDe150] = useState(false);
  const [arrastandoRelatorio, setArrastandoRelatorio] = useState(false);
  const [progressoSalvamento, setProgressoSalvamento] = useState(0);
  const [etapaSalvamento, setEtapaSalvamento] = useState("");
  const [notificacao, setNotificacao] = useState({
    aberta: false,
    mensagem: "",
    tipo: "success",
  });
  const dados = useMemo(() => calcularDados(dosagem || {}, materiais), [dosagem, materiais]);

  useEffect(() => {
    instalarEstiloPrintDosagem();
    return () => document.getElementById("print-style-dosagem")?.remove();
  }, []);

  useEffect(() => {
    const atualizarZoom = () => {
      const zoom = window.innerWidth > 0
        ? window.outerWidth / window.innerWidth
        : 1;
      setZoomAcimaDe150(zoom > 1.5);
    };

    atualizarZoom();
    window.addEventListener("resize", atualizarZoom);
    return () => window.removeEventListener("resize", atualizarZoom);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("relatorio-dosagem-zoom-acima-150", zoomAcimaDe150);
    return () => document.body.classList.remove("relatorio-dosagem-zoom-acima-150");
  }, [zoomAcimaDe150]);

  function iniciarArrasteRelatorio(event) {
    if (!zoomAcimaDe150 || event.button !== 0) return;
    if (event.target.closest?.("button, a, input, select, textarea, [role='button']")) return;

    const relatorio = relatorioRef.current;
    if (!relatorio) return;

    relatorio.setPointerCapture(event.pointerId);
    arrasteRelatorioRef.current = {
      pointerId: event.pointerId,
      inicioX: event.clientX,
      inicioY: event.clientY,
      scrollLeft: relatorio.scrollLeft,
      scrollTop: relatorio.scrollTop,
    };
    setArrastandoRelatorio(true);
  }

  function moverRelatorioArrastado(event) {
    const arraste = arrasteRelatorioRef.current;
    const relatorio = relatorioRef.current;
    if (!arraste || !relatorio || arraste.pointerId !== event.pointerId) return;

    event.preventDefault();
    relatorio.scrollLeft = arraste.scrollLeft - (event.clientX - arraste.inicioX);
    relatorio.scrollTop = arraste.scrollTop - (event.clientY - arraste.inicioY);
  }

  function finalizarArrasteRelatorio(event) {
    const relatorio = relatorioRef.current;
    if (arrasteRelatorioRef.current?.pointerId !== event.pointerId) return;

    if (relatorio?.hasPointerCapture(event.pointerId)) {
      relatorio.releasePointerCapture(event.pointerId);
    }
    arrasteRelatorioRef.current = null;
    setArrastandoRelatorio(false);
  }

  if (!dosagem) return null;

 async function gerarPdfLaudo(onProgresso, nomeArquivoPdf = nomeArquivoRelatorioDosagem(dosagem)) {
  const paginas = relatorioRef.current?.querySelectorAll(
    ":scope > .pagina, .relatorio-guia-dosagem-anexo .guia-pagina, .certificado-prensa-anexo .relatorio-calibracao-print"
  );

  if (!paginas?.length) {
    throw new Error("Nenhuma página encontrada para gerar o PDF.");
  }

  let pdf = null;

 
  const restaurarImagens = await prepararImagensParaPdf(relatorioRef.current);

  const adicionarCanvasAoPdf = (canvas, orientacao) => {
    const larguraFolha = orientacao === "landscape" ? 297 : 210;
    const alturaFolha = orientacao === "landscape" ? 210 : 297;

    if (!pdf) {
      pdf = new jsPDF({
        orientation: orientacao,
        unit: "mm",
        format: "a4",
        putOnlyUsedFonts: true,
        compress: true,
      });
    } else {
      pdf.addPage("a4", orientacao);
    }

    pdf.addImage(
      canvas.toDataURL("image/png"),
      "PNG",
      0,
      0,
      larguraFolha,
      alturaFolha,
      undefined,
      "SLOW"
    );
  };

  const adicionarCanvasFatiadoAoPdf = (canvas, orientacao, paginaOriginal) => {
  const larguraFolha = orientacao === "landscape" ? 297 : 210;
  const alturaFolha = orientacao === "landscape" ? 210 : 297;

  const pageWidthPx = canvas.width;
  const pageHeightPx = Math.floor(pageWidthPx * (alturaFolha / larguraFolha));

  const pxPorMm = pageWidthPx / larguraFolha;
  const margemInferiorPx = Math.ceil(8 * pxPorMm);

  const escala = canvas.width / paginaOriginal.offsetWidth;

  const linhaExecucao = paginaOriginal.querySelector(".linha-execucao-traco");
  const rodape = paginaOriginal.querySelector(".rodape-restrito");

  const topoPagina = paginaOriginal.getBoundingClientRect().top;

  const alturaCabecalhoPx = linhaExecucao
    ? Math.ceil(
        (linhaExecucao.getBoundingClientRect().bottom - topoPagina) * escala
      )
    : 0;

  const rodapeRect = rodape?.getBoundingClientRect();

  const rodapeTopPx = rodapeRect
    ? Math.floor((rodapeRect.top - topoPagina) * escala)
    : canvas.height;

  const alturaRodapePx = rodapeRect
    ? Math.ceil(rodapeRect.height * escala)
    : 0;

  const limiteConteudoPx = rodapeTopPx;

  const blocosQueNaoPodemCortar = Array.from(
  paginaOriginal.querySelectorAll(
   ".titulo-secao, .subtitulo-faixa, .texto, .lista-normas, .caracteristica, .tabela-materiais, .bloco-rupturas, .padiola-card-relatorio, .assinatura-tecnica"
  )
)
  .map((el) => {
    const rect = el.getBoundingClientRect();
    return {
      top: Math.floor((rect.top - topoPagina) * escala),
      bottom: Math.ceil((rect.bottom - topoPagina) * escala),
    };
  })
  .filter((item) => item.bottom > 0 && item.top < limiteConteudoPx);

const ajustarCorteParaNaoCortarBloco = (inicioPx, corteDesejadoPx) => {
  const blocoCortado = blocosQueNaoPodemCortar.find(
    (bloco) => bloco.top < corteDesejadoPx && bloco.bottom > corteDesejadoPx
  );

  if (!blocoCortado) return corteDesejadoPx;

  if (blocoCortado.top > inicioPx + 20) {
    return blocoCortado.top;
  }

  return corteDesejadoPx;
};

  let posY = 0;
  let numeroPagina = 0;

  while (posY < limiteConteudoPx) {
    const temCabecalho = numeroPagina > 0 && alturaCabecalhoPx > 0;

    const alturaTopoPx = temCabecalho ? alturaCabecalhoPx : 0;

    const alturaReservadaRodapePx = alturaRodapePx + margemInferiorPx;

    const alturaUtilPx =
      pageHeightPx - alturaTopoPx - alturaReservadaRodapePx;

    const corteDesejadoPx = Math.min(
  posY + alturaUtilPx,
  limiteConteudoPx
);

const corteAjustadoPx = ajustarCorteParaNaoCortarBloco(
  posY,
  corteDesejadoPx
);

const alturaAtual = Math.max(
  1,
  corteAjustadoPx - posY
);

    const canvasTemp = document.createElement("canvas");
    canvasTemp.width = pageWidthPx;
    canvasTemp.height = pageHeightPx;

    const ctx = canvasTemp.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvasTemp.width, canvasTemp.height);

    if (temCabecalho) {
      ctx.drawImage(
        canvas,
        0,
        0,
        pageWidthPx,
        alturaCabecalhoPx,
        0,
        0,
        pageWidthPx,
        alturaCabecalhoPx
      );
    }

    ctx.drawImage(
      canvas,
      0,
      posY,
      pageWidthPx,
      alturaAtual,
      0,
      alturaTopoPx,
      pageWidthPx,
      alturaAtual
    );

    if (alturaRodapePx > 0) {
      const destinoRodapeY = pageHeightPx - alturaRodapePx - margemInferiorPx;

      ctx.drawImage(
        canvas,
        0,
        rodapeTopPx,
        pageWidthPx,
        alturaRodapePx,
        0,
        destinoRodapeY,
        pageWidthPx,
        alturaRodapePx
      );
    }

    if (!pdf) {
      pdf = new jsPDF({
        orientation: orientacao,
        unit: "mm",
        format: "a4",
        putOnlyUsedFonts: true,
        compress: true,
      });
    } else {
      pdf.addPage("a4", orientacao);
    }

    pdf.addImage(
      canvasTemp.toDataURL("image/png"),
      "PNG",
      0,
      0,
      larguraFolha,
      alturaFolha,
      undefined,
      "SLOW"
    );

    posY += alturaAtual;
    numeroPagina += 1;
  }
};

  try {
    await aguardarImagensRelatorio();
    await aguardarRenderizacao();

    for (let index = 0; index < paginas.length; index += 1) {
      onProgresso?.(index, paginas.length);

      const pagina = paginas[index];

      const paginaGuia =
        pagina.classList.contains("guia-pagina") ||
        pagina.closest(".relatorio-guia-dosagem-anexo");

      const orientacao = paginaGuia ? "landscape" : "portrait";

      const canvas = await html2canvas(pagina, {
          backgroundColor: "#ffffff",
          scale: 3,
          useCORS: true,
        allowTaint: false,
        imageTimeout: 30000,
        scrollX: 0,
        scrollY: 0,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
        onclone: (documentoClonado) => {
          const paginasClonadas = documentoClonado.querySelectorAll(
            ".pagina-laudo-dosagem, .pagina-fotografica, .guia-pagina, .relatorio-calibracao-print"
          );

          paginasClonadas.forEach((paginaClonada) => {
  paginaClonada.style.setProperty("margin", "0", "important");
  paginaClonada.style.setProperty("box-sizing", "border-box", "important");
  paginaClonada.style.setProperty("overflow", "visible", "important");
});

         const paginasDosagem = documentoClonado.querySelectorAll(
  ".pagina-laudo-dosagem"
);

paginasDosagem.forEach((paginaClonada) => {
  paginaClonada.style.setProperty("width", "210mm", "important");
  paginaClonada.style.setProperty("min-height", "297mm", "important");
  paginaClonada.style.setProperty("height", "auto", "important");
  paginaClonada.style.setProperty("padding", "4mm 10mm", "important");
  paginaClonada.style.setProperty("overflow", "visible", "important");
});

const paginasFotograficas = documentoClonado.querySelectorAll(
  ".pagina-fotografica"
);

paginasFotograficas.forEach((paginaClonada) => {
  paginaClonada.style.setProperty("width", "210mm", "important");
  paginaClonada.style.setProperty("min-height", "297mm", "important");
  paginaClonada.style.setProperty("height", "297mm", "important");
  paginaClonada.style.setProperty("padding", "4mm 5mm", "important");
  paginaClonada.style.setProperty("overflow", "hidden", "important");
});

          const paginasGuia = documentoClonado.querySelectorAll(".guia-pagina");

          paginasGuia.forEach((paginaClonada) => {
            paginaClonada.style.setProperty("width", "297mm", "important");
            paginaClonada.style.setProperty("min-height", "210mm", "important");
            paginaClonada.style.setProperty("height", "210mm", "important");
            paginaClonada.style.setProperty("padding", "4mm 5mm", "important");
          });
        },
      });

     if (pagina.classList.contains("pagina-laudo-dosagem")) {
  adicionarCanvasFatiadoAoPdf(canvas, orientacao, pagina);
} else {
  adicionarCanvasAoPdf(canvas, orientacao);
}
    }

    onProgresso?.(paginas.length, paginas.length);
  } finally {
    restaurarImagens();
  }
const tituloPdf = nomeSeguroArquivo(
  String(nomeArquivoPdf).replace(/\.pdf$/i, "")
);

pdf.setProperties({
  title: tituloPdf,
  subject: "Relatório de Dosagem",
  author: NOME_CLIENTE_DOCUMENTO,
  creator: `SGQ ${NOME_CLIENTE_DOCUMENTO}`,
});
  return pdf.output("blob");
}

async function imprimirRelatorioCompleto() {
  if (imprimindo || salvandoLaudo) return;

  setImprimindo(true);

  const tituloAnterior = document.title;
  const nomeArquivo = nomeArquivoRelatorioDosagem(dosagem);
  const tituloImpressao = nomeArquivo.replace(/\.pdf$/i, "");

  try {
    instalarEstiloPrintDosagem();
    await aguardarImagensRelatorio();

    document.title = tituloImpressao;

    const blob = await gerarPdfLaudo(undefined, nomeArquivo);
    const url = URL.createObjectURL(blob);

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.title = tituloImpressao;
    iframe.src = url;

    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();

      setTimeout(() => {
        URL.revokeObjectURL(url);
        iframe.remove();
        document.title = tituloAnterior;
        setImprimindo(false);
      }, 3000);
    };

    document.body.appendChild(iframe);
  } catch (error) {
    console.error("Erro ao imprimir relatório:", error);
    document.title = tituloAnterior;
    setImprimindo(false);

    setNotificacao({
      aberta: true,
      mensagem: "Não foi possível gerar o PDF para impressão.",
      tipo: "error",
    });
  }
}

  async function salvarNosLaudos() {
    if (somenteLeitura) {
      setNotificacao({
        aberta: true,
        mensagem: "Seu acesso permite apenas visualizar e imprimir o relatório.",
        tipo: "warning",
      });
      return;
    }

    if (salvandoLaudo || laudoEnviado) return;

    if (!dosagem.obraId) {
      setNotificacao({
        aberta: true,
        mensagem: "Não foi possível identificar a obra desta dosagem.",
        tipo: "error",
      });
      return;
    }

    if (!auth.currentUser) {
      setNotificacao({
        aberta: true,
        mensagem: "Usuário não autenticado.",
        tipo: "error",
      });
      return;
    }

    setSalvandoLaudo(true);
    setProgressoSalvamento(5);
    setEtapaSalvamento("Preparando o relatório...");

    try {
      const identificacao = dadosLaudoDosagem(dosagem);
      setEtapaSalvamento("Verificando relatórios existentes...");

      const snapshot = await getDocs(
        consultaTenantAtual(query(collection(db, "laudos"), where("obraId", "==", dosagem.obraId)))
      );
      const relatoriosIguais = snapshot.docs.filter((documento) => {
        const laudo = documento.data();

        return (
          laudo.tipo === TIPO_LAUDO_DOSAGEM &&
          String(laudo.numeroTraco || "").toUpperCase() ===
            identificacao.numeroTraco &&
          String(laudo.numeroRevisao || "") === identificacao.numeroRevisao
        );
      });
      const relatorioAtivoExistente = relatoriosIguais.find(
        (documento) => documento.data().cancelado !== true
      );

      if (relatorioAtivoExistente) {
        setNotificacao({
          aberta: true,
          mensagem:
            "Este relatório já existe nos laudos. Para enviar uma atualização, altere o número da revisão. Um relatório com a mesma revisão só pode ser enviado novamente quando o anterior estiver cancelado nos laudos.",
          tipo: "warning",
        });
        return;
      }

      await aguardarRenderizacao();

      const sufixoArquivo = relatoriosIguais.length ? `-${Date.now()}` : "";
      const nomeArquivoBase = nomeArquivoRelatorioDosagem({
  ...dosagem,
  traco: identificacao.descricao,
}).replace(/\.pdf$/i, "");

const nomeArquivo = `${nomeArquivoBase}${sufixoArquivo}.pdf`;
      const storagePath = `dosagens/${dosagem.id}/laudos/${nomeArquivo}`;
      const pdf = await gerarPdfLaudo((paginaAtual, totalPaginas) => {
  const percentual = 10 + Math.round((paginaAtual / totalPaginas) * 60);
  setProgressoSalvamento(percentual);
  setEtapaSalvamento(
    `Gerando PDF: página ${Math.min(paginaAtual + 1, totalPaginas)} de ${totalPaginas}...`
  );
}, nomeArquivo);

      setProgressoSalvamento(75);
      setEtapaSalvamento("Enviando o relatório...");
      await uploadBytes(ref(storage, storagePath), pdf, {
        contentType: "application/pdf",
      });

      setProgressoSalvamento(90);
      setEtapaSalvamento("Registrando nos laudos...");
      const dadosLaudo = {
        obraId: dosagem.obraId,
        tipo: TIPO_LAUDO_DOSAGEM,
        descricao: identificacao.descricao,
        numeroTraco: identificacao.numeroTraco,
        numeroRevisao: identificacao.numeroRevisao,
        dosagemId: dosagem.id,
        materiaisTraco: {
          cimento: formatarMateriaisComConsumo(dosagem, "cimento"),
          adicoes: formatarMateriaisComConsumo(dosagem, "adicoesResumo"),
          agua: formatarMateriaisComConsumo(dosagem, "agua"),
          aditivos: formatarMateriaisComConsumo(dosagem, "aditivosResumo"),
          agregados: formatarMateriaisComConsumo(dosagem, "materiaisResumo"),
        },
        nomeArquivo,
        storagePath,
        cancelado: false,
        criadoPor: auth.currentUser.uid,
        atualizadoEm: serverTimestamp(),
      };

      await addDoc(collection(db, "laudos"), aplicarTenantAtual({
        ...dadosLaudo,
        criadoEm: serverTimestamp(),
      }));

      setProgressoSalvamento(100);
      setEtapaSalvamento("Relatório salvo com sucesso.");
      setLaudoEnviado(true);
      setNotificacao({
        aberta: true,
        mensagem: `${identificacao.descricao} foi salvo e anexado aos laudos.`,
        tipo: "success",
      });
    } catch (error) {
      console.error("Erro ao salvar relatório de dosagem:", error);
      setNotificacao({
        aberta: true,
        mensagem: error.message || "Não foi possível salvar o relatório nos laudos.",
        tipo: "error",
      });
    } finally {
      setSalvandoLaudo(false);
    }
  }
const textoIdadesRuptura = dados.idades
  .filter((idade) => idade.valor)
  .map((idade) => String(idade.valor).padStart(2, "0"))
  .join(", ")
  .replace(/,([^,]*)$/, " e$1");
  return (
    <div
      className={`relatorio-dosagem-print${dosagem.caldaCimento ? " relatorio-calda-cimento-print" : ""}${zoomAcimaDe150 ? " relatorio-dosagem-arrastavel" : ""}${arrastandoRelatorio ? " relatorio-dosagem-arrastando" : ""}`}
      ref={relatorioRef}
      onPointerDown={iniciarArrasteRelatorio}
      onPointerMove={moverRelatorioArrastado}
      onPointerUp={finalizarArrasteRelatorio}
      onPointerCancel={finalizarArrasteRelatorio}
    >
      {(!zoomAcimaDe150 || salvandoLaudo) && (
      <div className="acoes-print">
        {!zoomAcimaDe150 && <div className="acoes-print-botoes">
          {onVoltar && (
            <button
  className="btn-voltar"
  onClick={onVoltar}
  disabled={salvandoLaudo || imprimindo}
>
  Voltar
</button>
          )}
          <button
  className="btn-imprimir"
  onClick={imprimirRelatorioCompleto}
  disabled={salvandoLaudo || imprimindo}
>
  {imprimindo ? "Aguarde..." : "Imprimir"}
</button>
          {!somenteLeitura && (
            <button
  className="btn-salvar-laudo"
  onClick={salvarNosLaudos}
  disabled={salvandoLaudo || laudoEnviado || imprimindo}
>
  {salvandoLaudo
    ? "Salvando..."
    : laudoEnviado
      ? "Relatório salvo"
      : "Salvar nos Laudos"}
</button>
          )}
        </div>}

        {salvandoLaudo && (
          <div className="relatorio-salvamento-progresso">
            <div>
              <span>{etapaSalvamento}</span>
              <strong>{progressoSalvamento}%</strong>
            </div>
            <LinearProgress
              variant="determinate"
              value={progressoSalvamento}
              aria-label="Progresso do salvamento do relatório"
            />
          </div>
        )}
      </div>
      )}

      <Snackbar
        open={notificacao.aberta}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}
      >
        <Alert
          severity={notificacao.tipo}
          variant="filled"
          onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}
          sx={{ width: "100%" }}
        >
          {notificacao.mensagem}
        </Alert>
      </Snackbar>

      {dosagem.caldaCimento && (
        <PaginaRelatorioCaldaCimento dosagem={dosagem} dados={dados} />
      )}

      {!dosagem.caldaCimento && (
      <div className="pagina pagina-laudo-dosagem">
        <table className="cabecalho cabecalho-modelo">
          <tbody>
            <tr>
              <td rowSpan="3" className="logo-cell"><LogoRelatorio alt="Logo" className="logo"  /></td>
              <td className="titulo-sgq">SISTEMA DE GESTÃO DA QUALIDADE</td>
              <td className="codigo-cell">RQ 11.1</td>
            </tr>
            <tr>
              <td rowSpan="2" className="titulo-documento">Concreto - Laudo de dosagem - Carta traço</td>
              <td>Revisão 00</td>
            </tr>
            <tr>
              <td>20/08/2025</td>
            </tr>
          </tbody>
        </table>

        <table className="linha-data-codigo">
          <tbody>
            <tr>
              <td><strong>DATA</strong> <span>{dataAtualBR()}</span></td>
              <td>
                <strong>CÓDIGO:</strong>{" "}
                {formatarCodigoDosagem(
                  dosagem.codigo,
                  dosagem.numeroRevisao ?? dosagem.traco
                )}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="faixa-cinza"></div>

        <div className="bloco-empresa">
          <div><strong>INTERESSADO:</strong> <span>{dosagem.clienteNome || "-"}</span></div>
          <div><strong>OBRA:</strong> <span>{dosagem.obraNome || "-"}</span></div>
        </div>
        <div className="linha-execucao-traco">
          <span>Data da execução do traço:</span>
          <strong>{formatarDataBR(dosagem.data)}</strong>
        </div>

        <div className="faixa-relatorio-ensaio">RELATÓRIO DE ENSAIO</div>

        <SecaoTitulo numero="1.0">OBJETIVO</SecaoTitulo>
        <p className="texto">Este relatório tem por finalidade apresentar os resultados obtidos referentes ao estudo de concreto com materiais coletados e enviados pelo interessado, executado um traço experimental com base nos parâmetros fixados para a dosagem requerida.</p>
        <p className="texto">
  A dosagem foi executada em betoneira estacionária com capacidade real de 50 litros,
  após a mistura de cada um dos materiais realizou-se ensaio para determinação das
  propriedades do concreto no estado fresco.
</p>

<p className="texto">
  Com obtenção do abatimento requerido no traço moldaram-se corpos de prova
  (10x20 cm), conforme estabelecido pela NBR 5738 para determinação das
  resistências à compressão nas idades de {textoIdadesRuptura} dias, em
  consonância com a NBR 5739. Após 24 horas os corpos de prova foram
  desmoldados e colocados em tanques com água, permanecendo em cura até
  a idade de ruptura.
</p>

        <SecaoTitulo numero="2.0">METODOLOGIA</SecaoTitulo>
        <p className="texto">Os ensaios seguiram as prescrições das normas da ABNT:</p>
       <NormasMetodologia
  caldaCimento={Boolean(dosagem.caldaCimento)}
  metodologiasRelatorio={dosagem.metodologiasRelatorio}
/>
        <SecaoTitulo numero="3.0">CARACTERÍSTICA DO CONCRETO</SecaoTitulo>
        <table className="caracteristica caracteristica-concreto tabela-compacta">
          <colgroup>
            <col className="col-caracteristica-label" />
            <col className="col-caracteristica-traco" />
            <col className="col-caracteristica-label" />
            <col className="col-caracteristica-tipo" />
            <col className="col-caracteristica-label" />
            <col className="col-caracteristica-fck" />
            <col className="col-caracteristica-label col-caracteristica-label-slump" />
            <col className="col-caracteristica-slump" />
            <col className="col-caracteristica-label-teor" />
            <col className="col-caracteristica-teor" />
          </colgroup>
          <tbody>
            <tr>
              <td className="celula-label">Traço</td><td className="celula-valor texto-nowrap">{dosagem.traco || "-"}</td>
              <td className="celula-label">Tipo</td><td className="celula-valor texto-nowrap">{dosagem.tipoConcreto || "-"}</td>
              <td className="celula-label">fck</td><td className="celula-valor texto-nowrap">{fckPrincipal(dosagem.fck)} MPa</td>
              <td className="celula-label">Slump</td>
              <td className="celula-valor texto-nowrap">
                {dosagem.slump
                  ? `${dosagem.slump}${dosagem.toleranciaSlumpProjeto ? ` ± ${dosagem.toleranciaSlumpProjeto}` : ""} mm`
                  : "-"}
              </td>
              <td className="celula-label">Teor arg.</td><td className="celula-valor texto-nowrap">{numeroBR(dados.teorArgamassa, 1)}%</td>
            </tr>
          </tbody>
        </table>

        <Subtitulo numero="3.1">Materiais e consumo</Subtitulo>
        <table className="tabela-compacta tabela-materiais">
          <colgroup>
            <col className="col-materiais-material" />
            <col className="col-materiais-fornecedor" />
            <col className="col-materiais-fabricante" />
            <col className="col-materiais-consumo" />
            <col className="col-materiais-massa" />
            <col className="col-materiais-volume" />
          </colgroup>
          <thead>
            <tr className="subtitulo">
              <th>Materiais</th>
              <th>Fornecedor/Tipo</th>
              <th>Fabricante e/ou Jazida</th>
              <th>Consumo<br />(kg/m³)</th>
              <th>M. Esp.<br />(g/cm³)</th>
              <th>Volume<br />(L/m³)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="material-cimento">CIMENTO</td><td>{dados.cimento.descricao || "-"}</td><td>{dados.cimento.fornecedor || "-"}</td>
              <td>{numeroBR(dados.pesoCimento, 0)}</td><td>{valorOuTraco(dados.mEspCimento)}</td><td>{numeroBR(dados.volumeCimento, 1)}</td>
            </tr>

           {dados.adicoes.map((adicao, index) => (
  <tr key={adicao.id || `adicao-${index}`}>
    <td>
      <MaterialComParametro
        nome={dados.adicoes.length > 1 ? `ADIÇÃO ${index + 1}` : "ADIÇÃO"}
        parametro={`${numeroBR(dados.percentualAdicaoCorrigido(index), 1)}%`}
      />
    </td>
    <td>{adicao.descricao || adicao.material || adicao.nome || "-"}</td>
    <td>{adicao.fornecedor || adicao.fabricante || adicao.Fornecedor || "-"}</td>
    <td>{numeroBR(dados.pesoAdicao(index), 1)}</td>
    <td>{valorOuTraco(adicao.massaEspecifica)}</td>
    <td>{numeroBR(dados.volumeAdicao(index), 1)}</td>
  </tr>
))}

            {dados.agregadosRelatorio.map((material) => {
              const index = material.indiceOriginal;
              return (
              <tr key={material.id || index}>
                <td>
                  <MaterialComParametro
                   nome={
                    material.descricao ||
                    material.Descricao ||
                    material.Descrição ||
                    material.descricaoMaterial ||
                    material.materialDescricao ||
                    material.material ||
                    material.amostra ||
                    (material.tipoAgregado === "miudo" ? "AREIA" : "BRITA")
                  }
                    parametro={`${numeroBR(dados.percentualParcialAgregado(material, index), 1)}%`}
                  />
                </td>
                <td>{material.procedencia}</td><td>{material.localColeta}</td>
                <td>{numeroBR(dados.pesoAgregado(index), 0)}</td><td>{numeroBR(dados.massaEspecificaAgregado(material), 3)}</td><td>{numeroBR(dados.volumeAgregado(material, index), 1)}</td>
              </tr>
              );
            })}
            <tr><td><MaterialComParametro nome="ÁGUA" parametro={`a/c: ${numeroBR(dados.fatorAcCorrigido, 2)}`} /></td><td>{dados.agua.descricao || "-"}</td><td>{dados.agua.fornecedor || dados.agua.localColeta || "-"}</td><td>{numeroBR(dados.pesoAgua, 0)}</td><td>{numeroBR(dados.mEspAgua, 3)}</td><td>{numeroBR(dados.volumeAgua, 1)}</td></tr>
            {dados.aditivos.map((aditivo, index) => (
              <tr key={aditivo.id || index}>
                <td><MaterialComParametro nome={`ADITIVO ${index + 1}`} parametro={`${numeroBR(dados.percentualAditivoCorrigido(index), 2)}%`} /></td><td>{aditivo.descricao || "-"}</td><td>{aditivo.fornecedor || "-"}</td>
                <td>{numeroBR(dados.pesoAditivo(index), 1)}</td><td>{valorOuTraco(aditivo.massaEspecifica)}</td><td>{numeroBR(dados.volumeAditivo(index), 1)}</td>
              </tr>
            ))}
            <tr><td><MaterialComParametro nome="TEOR DE AR" parametro={`${numeroBR(dados.teorArConsiderado, 1)}%`} /></td><td>-</td><td>-</td><td>-</td><td>-</td><td>{numeroBR(dados.volumeArConsiderado, 1)}</td></tr>
           
                       <tr className="total">
              <td colSpan="3">Soma</td>
              <td>{numeroBR(dados.somaPesos, 0)}</td>
              <td>-</td>
              <td>1.000</td>
            </tr>
          </tbody>
        </table>

        <div className="bloco-rupturas">
          <Subtitulo numero="3.2">
            Dados das rupturas
          </Subtitulo>

          <table className="resistencia tabela-compacta">
            <thead>
              <tr>
                <th colSpan={dados.idades.length + 1}>
                  RESISTÊNCIA À COMPRESSÃO OBTIDOS
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>IDADE (DIAS)</td>
                {dados.idades.map((idade) => (
                  <td key={`idade-${idade.index}`}>
                    {idade.valor || "-"}
                  </td>
                ))}
              </tr>

              <tr>
                <td>DATA</td>
                {dados.idades.map((idade) => (
                  <td key={`data-${idade.index}`}>
                    {idade.valor
                      ? adicionarDiasDataCurta(dosagem.data, idade.valor)
                      : "-"}
                  </td>
                ))}
              </tr>

              <tr>
                <td>fck (MPa)</td>
                {dados.idades.map((idade) => (
                  <td key={`fck-${idade.index}`}>
                    <CelulaFck dados={dados} idade={idade} />
                  </td>
                ))}
              </tr>

              <tr>
                <td>Gráfico</td>
                <td colSpan={dados.idades.length}>
                  <GraficoResistencia dados={dados} />
                </td>
              </tr>

              <tr>
                <td>Crescimento (%)</td>
                {dados.idades.map((idade) => (
                  <td key={`cres-${idade.index}`}>
                    {idade.index === 0 || !idade.valor
                      ? "-"
                      : `${numeroBR(dados.crescimento(idade), 1)}%`}
                  </td>
                ))}
              </tr>

              <tr>
                <td
                  colSpan={dados.idades.length + 1}
                  className="resistencia-rodape-cell"
                >
                  <RodapeResistencia dosagem={dosagem} dados={dados} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {mostrarDosagemVolume && (
          <div className="bloco-materiais-volume">
            <Subtitulo numero="3.3">MATERIAIS E CONSUMO EM VOLUME</Subtitulo>
            <table className="tabela-compacta tabela-materiais tabela-materiais-volume">
              <thead>
                <tr className="subtitulo">
                  <th>Material</th>
                  <th>Quantidade por saco<br />(kg)</th>
                  <th>Massa unitária<br />(kg/L)</th>
                  <th>Volume<br />(L)</th>
                  <th>Quantidade<br />de padiolas</th>
                  <th>Medidas A × B × H<br />(cm)</th>
                  <th>Massa por padiola<br />(kg)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="material-cimento">CIMENTO</td>
                  <td>{numeroBR(dados.pesoSacoCimento, 1)}</td>
                  <td>-</td>
                  <td>{numeroBR(dados.volumeCimento * dados.fatorSacoCimento, 1)}</td>
                  <td>1 saco</td>
                  <td>-</td>
                  <td>-</td>
                </tr>

                {dados.adicoes.map((adicao, index) => (
                  <tr key={adicao.id || `adicao-volume-${index}`}>
                    <td>{dados.adicoes.length > 1 ? `ADIÇÃO ${index + 1}` : "ADIÇÃO"}</td>
                    <td>{numeroBR(dados.pesoAdicao(index) * dados.fatorSacoCimento, 1)}</td>
                    <td>-</td>
                    <td>{numeroBR(dados.volumeAdicao(index) * dados.fatorSacoCimento, 1)}</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                ))}

                {dados.agregadosRelatorio.map((material) => {
                  const index = material.indiceOriginal;
                  const quantidade = dados.quantidadePadiolas(index);
                  const pesoPorSaco = dados.pesoAgregadoPorSaco(index);
                  return (
                    <tr key={`${material.id || "agregado"}-volume-${index}`}>
                      <td>{material.material || (material.tipoAgregado === "miudo" ? "AREIA" : "BRITA")}</td>
                      <td>{numeroBR(pesoPorSaco, 1)}</td>
                      <td>{numeroBR(material.massaUnitaria, 3)}</td>
                      <td>{numeroBR(dados.volumeAgregadoPorSaco(material, index), 1)}</td>
                      <td>{numeroBR(quantidade, 0)}</td>
                      <td>
                        {numeroBR(dados.medidaPadiolaA(index), 0)} × {numeroBR(dados.medidaPadiolaB(index), 0)} × {numeroBR(dados.alturaPadiola(material, index), 1)}
                      </td>
                      <td>{numeroBR(quantidade ? pesoPorSaco / quantidade : null, 1)}</td>
                    </tr>
                  );
                })}

                <tr>
                  <td>ÁGUA</td>
                  <td>{numeroBR(dados.pesoAgua * dados.fatorSacoCimento, 1)}</td>
                  <td>-</td>
                  <td>{numeroBR(dados.volumeAgua * dados.fatorSacoCimento, 1)}</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                </tr>

                {dados.aditivos.map((aditivo, index) => (
                  <tr key={aditivo.id || `aditivo-volume-${index}`}>
                    <td>{`ADITIVO ${index + 1}`}</td>
                    <td>{numeroBR(dados.pesoAditivo(index) * dados.fatorSacoCimento, 2)}</td>
                    <td>-</td>
                    <td>{numeroBR(dados.volumeAditivo(index) * dados.fatorSacoCimento, 2)}</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="padiolas-relatorio-titulo">
              VOLUME DE 01 SACO DE CIMENTO DE {numeroBR(dados.pesoSacoCimento, 0)} kg
            </div>
            <div className="padiolas-grid padiolas-grid-relatorio">
              {dados.agregadosRelatorio.map((material, ordemRelatorio) => {
                const index = material.indiceOriginal;
                const quantidade = dados.quantidadePadiolas(index);
                const volumePorSaco = dados.volumeAgregadoPorSaco(material, index);
                return (
                  <div
                    className="padiola-card-relatorio"
                    key={`${material.id || "agregado"}-desenho-padiola-${index}`}
                    style={{ order: ordemRelatorio }}
                  >
                    <table className="padiola-relatorio-cabecalho">
                      <tbody>
                        <tr>
                          <th>{material.material || (material.tipoAgregado === "miudo" ? "AREIA" : "BRITA")}</th>
                          <td>Volume {numeroBR(volumePorSaco, 1)} L</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="padiola-relatorio-imagem">
                      <img
                        src={`${process.env.PUBLIC_URL}/Images/padiola-relatorio.png`}
                        alt="Padiola de madeira"
                      />
                    </div>
                    <table className="padiola-relatorio-dados">
                      <tbody>
                        <tr>
                          <th>{numeroBR(quantidade, 0)} {quantidade === 1 ? "Padiola" : "Padiolas"}</th>
                          <td>
                            {numeroBR(dados.medidaPadiolaA(index), 0)} × {numeroBR(dados.medidaPadiolaB(index), 0)} × {numeroBR(dados.alturaPadiola(material, index), 1)} cm
                          </td>
                        </tr>
                        <tr>
                          <th>Latas de 18 litros</th>
                          <td>{numeroBR(volumePorSaco !== null ? volumePorSaco / 18 : null, 1)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="observacoes-importantes">
          <SecaoTitulo numero="4.0">OBSERVAÇÕES IMPORTANTES</SecaoTitulo>
          {observacoesImportantes(dosagem).map((item, index) => (
            <React.Fragment key={`${item.titulo}-${index}`}>
              <Subtitulo numero={`4.${index + 1}`}>{item.titulo || "Observação"}</Subtitulo>
              {String(item.texto || "")
                .split(/\r?\n/)
                .filter((linha) => linha.trim())
                .map((linha, linhaIndex) => (
                  <p className="texto" key={`observacao-${index}-${linhaIndex}`}>{linha}</p>
                ))}
            </React.Fragment>
          ))}
        </div>

        {dosagem.prensaRuptura && (
          <>
            <SecaoTitulo numero="5.0">PRENSA UTILIZADA PARA RUPTURA</SecaoTitulo>
            <p className="texto">{formatarPrensaRuptura(dosagem.prensaRuptura)}</p>
          </>
        )}

        <AssinaturaResponsavelTecnico />
        <RodapeRestrito />
      </div>
      )}

      <RelatorioFotografico dosagem={dosagem} fotos={fotosRelatorio} />

      {!dosagem.caldaCimento && (
        <RelatorioGuiaDosagemPrint
          dosagem={dosagem}
          materiais={materiais}
          embedded
        />
      )}

      {!dosagem.caldaCimento && dosagem.prensaRuptura && (
        <div className="certificado-prensa-anexo">
          <RelatorioCalibracaoPrensaPrint dados={dosagem.prensaRuptura} />
        </div>
      )}
    </div>
  );
}
